import axiosLib, { type AxiosInstance } from 'axios'
import { type KeywordListEntity } from '../entities/keyword-list.entity'
import { type KeywordFlaggingDataSource, type SaveKeywordListEntityParams } from '../keyword-flagging.datasource'

interface BackendFlaggingRule {
  id: number
  name: string
  enabled: boolean
  patterns: string[]
  severity: 'auto_delete' | 'review'
  artist_id: number
  created_at: string | null
  updated_at: string | null
}

interface BackendResponse<T> {
  status: string
  data: T
}

interface ClubResponse {
  club: { id: number }
}

interface StrapiJwtResponse {
  jwt: string
}

/**
 * API datasource for keyword flagging that calls the Chorus backend-v2.
 *
 * Uses Clerk JWT to exchange for a Strapi JWT, then uses the Strapi JWT
 * for all backend-v2 API calls (backend-v2 verifies Strapi JWTs).
 */
export class KeywordFlaggingApiDataSource implements KeywordFlaggingDataSource {
  private clubIdCache = new Map<string, number>()
  private strapiJwt: string | null = null
  private readonly backendAxios: AxiosInstance

  constructor(
    private readonly baseUrl: string,
    private readonly clerkAxios: AxiosInstance,
    private readonly getOrganizationSlug: () => string | undefined
  ) {
    this.backendAxios = axiosLib.create()
  }

  private async getStrapiJwt(): Promise<string> {
    if (this.strapiJwt) return this.strapiJwt
    const response = await this.clerkAxios.post<StrapiJwtResponse>(`${this.baseUrl}/clerk/generate-strapi-jwt`)
    this.strapiJwt = response.data.jwt
    return this.strapiJwt
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const jwt = await this.getStrapiJwt()
    return { Authorization: `Bearer ${jwt}` }
  }

  private async resolveClubId(): Promise<number> {
    const slug = this.getOrganizationSlug()
    if (!slug) throw new Error('No organization selected')

    const cached = this.clubIdCache.get(slug)
    if (cached !== undefined) return cached

    const response = await this.backendAxios.get<ClubResponse>(`${this.baseUrl}/clubs/slug/${slug}`)
    const clubId = response.data.club.id
    this.clubIdCache.set(slug, clubId)
    return clubId
  }

  private toEntity(rule: BackendFlaggingRule): KeywordListEntity {
    return {
      id: String(rule.id),
      name: rule.name,
      keywords: rule.patterns,
      auto_moderation: rule.severity === 'auto_delete',
      date_modified: rule.updated_at ?? rule.created_at ?? new Date().toISOString()
    }
  }

  async getAll(): Promise<KeywordListEntity[]> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get<BackendResponse<BackendFlaggingRule[]>>(
      `${this.baseUrl}/clubs/${clubId}/flagging/rules`,
      { headers }
    )
    return response.data.data.map((rule) => this.toEntity(rule))
  }

  async getById(id: string): Promise<KeywordListEntity | undefined> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get<BackendResponse<BackendFlaggingRule[]>>(
      `${this.baseUrl}/clubs/${clubId}/flagging/rules`,
      { headers }
    )
    const rule = response.data.data.find((r) => r.id === Number(id))
    return rule ? this.toEntity(rule) : undefined
  }

  async save(entity: SaveKeywordListEntityParams): Promise<KeywordListEntity> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])

    const body = {
      name: entity.name,
      patterns: entity.keywords,
      severity: entity.auto_moderation ? ('auto_delete' as const) : ('review' as const)
    }

    if (entity.id) {
      const response = await this.backendAxios.patch<BackendResponse<BackendFlaggingRule>>(
        `${this.baseUrl}/clubs/${clubId}/flagging/rules/${entity.id}`,
        body,
        { headers }
      )
      return this.toEntity(response.data.data)
    }

    const response = await this.backendAxios.post<BackendResponse<BackendFlaggingRule>>(
      `${this.baseUrl}/clubs/${clubId}/flagging/rules`,
      body,
      { headers }
    )
    return this.toEntity(response.data.data)
  }

  async deleteById(id: string): Promise<void> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    await this.backendAxios.delete(`${this.baseUrl}/clubs/${clubId}/flagging/rules/${id}`, { headers })
  }
}
